import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { importBudget } from '../../../models/budgetsModel';

function BudgetYearImport({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    from_year: new Date().getFullYear().toString(),
    to_year: (new Date().getFullYear() + 1).toString(),
    target_year: '0' // 0 = Replace, 1 = Keep existing
  });
  const [isImporting, setIsImporting] = useState(false);

  const currentYear = new Date().getFullYear();
  // Generate years from 5 years ago to 10 years in the future
  const years = Array.from({ length: 16 }, (_, i) => currentYear - 5 + i);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to import budgets from <strong>${formData.from_year}</strong> to <strong>${formData.to_year}</strong>.<br/><br/>` +
            `This will <strong>${formData.target_year === '0' ? 'REPLACE existing data' : 'keep existing data'}</strong> in ${formData.to_year}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0087D4',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, import!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setIsImporting(true);

    try {
      const importFormData = new FormData();
      importFormData.append('from_year', formData.from_year);
      importFormData.append('to_year', formData.to_year);
      importFormData.append('target_year', formData.target_year);

      const data = await importBudget(importFormData);

      if (data.status === 1) {
        toast.success('Budgets imported successfully!');
        onSuccess?.();
        onClose();
      } else {
        toast.error(data.message || 'Failed to import budgets');
      }
    } catch (error) {
      console.error('Error importing budgets:', error);
      toast.error('Failed to import budgets. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h3 className="text-lg font-semibold text-gray-900">Import Year Budget</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            This feature imports all budgets from one year to another year. You can choose to replace or keep existing data.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* From Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Year
              </label>
              <select
                name="from_year"
                value={formData.from_year}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* To Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Year
              </label>
              <select
                name="to_year"
                value={formData.to_year}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Target Year Option */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Import Option
              </label>
              <select
                name="target_year"
                value={formData.target_year}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">Replace Existing Data</option>
                <option value="1">Keep Existing Data</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.target_year === '0' 
                  ? 'All existing budgets in the target year will be deleted and replaced.'
                  : 'Existing budgets will be kept, only missing months will be imported.'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isImporting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isImporting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              {isImporting ? 'Importing...' : 'Import Budgets'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BudgetYearImport;
